// AIDEV-NOTE: Single source of truth for the consultant-access flag key.
// Server loaders/actions AND client entry points both gate on it; it must fail
// closed (see modules.server/feature-flags/fail-modes.ts).
export const ENABLE_CONSULTANT_ACCESS_FLAG = "enable-consultant-access";

// AIDEV-NOTE: Client-data minimization flag — gates the content-category
// selection on grant CREATION only. Enforcement of stored categories at read
// time is data-driven and never flag-gated, so flipping this flag off can
// never widen an already-minimized grant. Fail-closed.
export const ENABLE_CONSULTANT_DATA_MINIMIZATION_FLAG =
    "enable-consultant-data-minimization";

// AIDEV-NOTE: Content categories a grant can include, in the product's terms
// (Adam, 2026-08-28). Practitioner-facing (notes, assessment, chat) vs client
// narrative (transcript, recording). Binary include/exclude by category — NOT
// content-aware redaction.
//
// "assessment" covers the scored surface AND its reflections/teachable moments:
// a scorecard without its moments is not a thing anyone wants to share. The
// former standalone "reflections" category is accepted as a legacy alias (see
// CONSULTANT_LEGACY_CATEGORY_ALIASES) so pre-existing grants keep resolving.
export const CONSULTANT_CONTENT_CATEGORIES = [
    "notes",
    "assessment",
    "chat",
    "transcript",
    "recording",
] as const;

// AIDEV-NOTE: Old stored values → current category. Read-side only.
export const CONSULTANT_LEGACY_CATEGORY_ALIASES: Record<string, string> = {
    reflections: "assessment",
};

export type ConsultantContentCategory =
    (typeof CONSULTANT_CONTENT_CATEGORIES)[number];

export const CONSULTANT_NARRATIVE_CATEGORIES: readonly ConsultantContentCategory[] =
    ["transcript", "recording"];

export const CONSULTANT_CONTENT_CATEGORY_LABELS: Record<
    ConsultantContentCategory,
    string
> = {
    notes: "Parts Companion session notes (summary, parts, map)",
    assessment: "My Parts Companion assessment",
    chat: "My chat with Parts Companion",
    transcript: "Transcript",
    recording: "Recording",
};

// AIDEV-NOTE: Per-grant client-consent attestation (Adam, 2026-10-19). Every
// new grant needs it, and unattested grants don't resolve at read time (see
// CONSENT_ATTESTED_PREDICATE in server/repository.ts). One label for every
// surface that asks, so the wording the practitioner agreed to is auditable.
export const CONSULTANT_CONSENT_ATTESTATION_LABEL =
    "I confirm I have the client consent that the law where I practice requires for sharing these sessions with this consultant.";

/** Stable error code the share/attest APIs return with a 400. */
export const CONSULTANT_CONSENT_REQUIRED_CODE = "consent_attestation_required";
